import { AsyncPipe, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, ElementRef, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { QuoteLeadUserTitle, StudentDetails } from '@lx/quote-tool/shared/types';
import { AcquisitionDisclaimerComponent } from '@lx/quote-tool/shared/ui/components';
import { BlockSpacesDirective } from '@lx/quote-tool/shared/ui/directives';
import { CountryRegion, CountryRegionTypeEnum, ResidenceCountry } from '@lx/shared/data-access/destination';
import { getCompany, getConfiguration } from '@lx/shared/tokens';
import { CodeAndName } from '@lx/shared/types';
import { ButtonComponent } from '@lx/shared/ui/button';
import { CompanyFormattedNamePipe } from '@lx/shared/ui/pipes';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { RadioButtonModule } from 'primeng/radiobutton';
import { Observable, skip, switchMap, withLatestFrom } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { FormErrorTrackerDirective } from '../../directives';
import { NavigationService, ResidenceDataService } from '../../services';
import { QuoteToolLiteStore } from '../../store';
import { RouteIds, Step } from '../../types';
import { allergyValidator, postalCodeValidator } from '../../validators';
import { customEmailValidator, minPhoneNumberLength } from '@lx/quote-tool/lite/shared/utils';
import { DateOfBirthFieldComponent } from '../date-of-birth-field/date-of-birth-field.component';
import { ErrorMessageComponent, FormFieldComponent, PhoneNumberFieldComponent, YesNoRadioButtonComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { MotherTongueSelectorComponent } from '../mother-tongue-selector/mother-tongue-selector.component';
import { NationalitySelectorComponent } from '../nationality-selector/nationality-selector.component';
import { RegionCountryFieldComponent } from '../region-country-field/region-country-field.component';
import { ResidenceCountrySelectorComponent } from '../residence-country-selector/residence-country-selector.component';
import { KeyboardFocusDirective } from '@lx/shared/ui/keyboard-focus';
import { Flows } from '@lx/quote-tool/lite/shared/types';

@Component({
  standalone: true,
  selector: 'lx-enrolment-form',
  imports: [
    ReactiveFormsModule,
    NgForOf,
    RadioButtonModule,
    InputTextModule,
    MotherTongueSelectorComponent,
    NationalitySelectorComponent,
    PhoneNumberFieldComponent,
    ResidenceCountrySelectorComponent,
    ButtonComponent,
    NgIf,
    RegionCountryFieldComponent,
    CheckboxModule,
    YesNoRadioButtonComponent,
    InputTextareaModule,
    FormFieldComponent,
    CompanyFormattedNamePipe,
    NgTemplateOutlet,
    DateOfBirthFieldComponent,
    ErrorMessageComponent,
    FormErrorTrackerDirective,
    AsyncPipe,
    BlockSpacesDirective,
    KeyboardFocusDirective,
    AcquisitionDisclaimerComponent,
  ],
  templateUrl: './enrolment-form.component.html',
  styleUrls: ['./enrolment-form.component.scss'],
})
export class EnrolmentFormComponent implements OnInit {
  @ViewChild('errorMessage') errorMessage: ElementRef;
  @Output() submitForm = new EventEmitter<void>();

  form: FormGroup;
  titleOptions: CodeAndName[] = [];
  regions: CountryRegion[] = [];
  countryRegionType: string | undefined;
  postalCodeRegion = false;
  whatsappOptIn = false;
  priceConfirmation = false;
  onlineFlow = false;
  residenceCountry: string | undefined;
  activeFlow: Flows | undefined;
  termsAndConditionsLink: string | undefined;
  privacyPolicyLink: string | undefined;
  submitted = false;
  currentStep$: Observable<Step> | undefined;
  readonly company = getCompany();

  protected regionFieldPlaceholder: string | undefined;
  protected readonly CountryRegionType = CountryRegionTypeEnum;
  protected readonly Flows = Flows;

  private readonly formBuilder = inject(FormBuilder);
  private readonly residenceDataService = inject(ResidenceDataService);
  private readonly navigationService = inject(NavigationService);
  private readonly qtLiteStore = inject(QuoteToolLiteStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly configuration = getConfiguration();
  private readonly localizedOptions: Record<QuoteLeadUserTitle, string> = {
    MR: $localize`Mr.`,
    MS: $localize`Ms.`,
    MRS: $localize`Mrs.`,
    MISS: $localize`Miss`,
  };

  protected readonly phoneCode = toSignal(this.qtLiteStore.phoneCode$);

  private readonly commonFieldValidators = {
    firstName: [Validators.required, Validators.maxLength(80)],
    lastName: [Validators.required, Validators.maxLength(40)],
    email: [Validators.required, customEmailValidator()],
    phoneNumber: [Validators.required, minPhoneNumberLength(5)],
  };

  private guardianFields = ['guardianFirstName', 'guardianLastName', 'guardianEmail', 'guardianPhone'];
  private emergencyFields = ['emergencyContactFirstName', 'emergencyContactLastName', 'emergencyContactEmail', 'emergencyContactPhone'];

  private get fields() {
    return this.form.controls;
  }

  ngOnInit() {
    this.initializeForm();
    this.setPhoneCodeFromStore();
    this.initializeTitleOptions();
    this.initializeWhatsappOptIn();
    this.loadInitialResidenceData();
    this.handleResidenceCountryChanges();
    this.currentStep$ = this.qtLiteStore.currentStep$;
    this.termsAndConditionsLink = <string>this.configuration.QUOTE_TOOL_TERMS_AND_CONDITIONS_LINK;
    this.privacyPolicyLink = <string>this.configuration.QUOTE_TOOL_PRIVACY_POLICY_LINK;
    this.qtLiteStore.activeFlow$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((activeFlow) => {
      this.activeFlow = activeFlow;
      this.onlineFlow = activeFlow === Flows.Online;
      if (activeFlow === Flows.Online) {
        this.disableFieldsForOnlineFlow();
      } else {
        const adultAgeGroup = activeFlow === Flows.Adult;
        if (adultAgeGroup) {
          this.setupAdultFieldListener();
        } else {
          this.toggleFields(this.guardianFields, true);
        }
        this.fields.isAdult.setValue(adultAgeGroup);
        this.fields.isAdult.markAsTouched();
        this.setupAllergyValidation(); // For non-online flows
      }
      this.populateFormWithStudentDetails();
    });
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateStore());

    this.setPhoneCodeFromStore();
  }

  backToQuote() {
    this.navigationService.navigateToStep(RouteIds.quote);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.submitted = true;
      this.form.markAllAsTouched();
      this.scrollToTop();
      return;
    }
    this.submitForm.emit();
  }

  private initializeForm() {
    this.form = this.formBuilder.group({
      title: ['', Validators.required],
      firstName: ['', this.commonFieldValidators.firstName],
      lastName: ['', this.commonFieldValidators.lastName],
      phoneNumber: ['', this.commonFieldValidators.phoneNumber],
      dateOfBirth: ['', Validators.required],
      motherTongue: ['', Validators.required],
      nationality: ['', Validators.required],
      email: ['', this.commonFieldValidators.email],
      address: ['', Validators.required],
      address2: [''],
      city: ['', Validators.required],
      regionStateProvince: [''],
      postcode: ['', [Validators.required, postalCodeValidator(this.postalCodeRegion)]],
      countryOfResidence: ['', Validators.required],
      hasAllergiesConditionsOrTreatments: [null, Validators.required],
      allergiesConditionsOrTreatments: [{ value: '', disabled: true }, Validators.required],
      specialRequests: [''],
      isAdult: [undefined, Validators.required],
      guardianFirstName: [{ value: '', disabled: true }, this.commonFieldValidators.firstName],
      guardianLastName: [{ value: '', disabled: true }, this.commonFieldValidators.lastName],
      guardianEmail: [{ value: '', disabled: true }, this.commonFieldValidators.email],
      guardianPhone: [{ value: '', disabled: true }, this.commonFieldValidators.phoneNumber],
      emergencyContactFirstName: [{ value: '', disabled: true }, this.commonFieldValidators.firstName],
      emergencyContactLastName: [{ value: '', disabled: true }, this.commonFieldValidators.lastName],
      emergencyContactEmail: [{ value: '', disabled: true }, this.commonFieldValidators.email],
      emergencyContactPhone: [{ value: '', disabled: true }, this.commonFieldValidators.phoneNumber],
      termsAndConditionsConsent: [undefined, Validators.required],
      hasMedicalInsuranceProof: [undefined, Validators.required],
      optOutMarketing: [false],
    });
  }

  private populateFormWithStudentDetails(): void {
    this.qtLiteStore.studentDetails$.pipe(take(1)).subscribe((studentDetails: StudentDetails) => {
      const countryOfResidence = studentDetails?.countryOfResidence || this.residenceDataService.detectCountryCode();

      const updatedStudentDetails = {
        ...studentDetails,
        optOutMarketing: studentDetails?.consentMarketing === false,
        countryOfResidence,
        isAdult: typeof studentDetails?.isMinor === 'boolean' ? !studentDetails?.isMinor : undefined,
        hasMedicalInsuranceProof: typeof studentDetails?.hasMedicalInsuranceProof === 'boolean' ? studentDetails?.hasMedicalInsuranceProof : undefined,
      };
      this.form.patchValue(updatedStudentDetails);
      this.markFormControlsAsTouched();
    });
  }

  private markFormControlsAsTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);

      // Check if the control has a value before marking it as touched
      if (control?.value !== null && control?.value !== undefined && control?.value !== '') {
        control.markAsTouched();
        control.updateValueAndValidity();

        // Handle phone number fields if applicable
        if (key === 'emergencyContactPhone' || key === 'guardianPhone' || key === 'phoneNumber') {
          this.handlePhoneNumberField(control.value, control);
        }
      }
    });
  }

  private handlePhoneNumberField(phoneNumber: string, control: AbstractControl): void {
    const [, phoneNumberPart] = phoneNumber.split(' '); // Only keep the phone number part

    if (!phoneNumberPart) {
      // Mark phone number as untouched if it's invalid or incomplete
      control.markAsUntouched();
    }
  }

  private getFormValues(): StudentDetails {
    const { isAdult, optOutMarketing, hasMedicalInsuranceProof, ...formValues } = this.form.value;
    return {
      ...formValues,
      isMinor: !isAdult,
      consentMarketing: !optOutMarketing,
      hasMedicalInsuranceProof: hasMedicalInsuranceProof === undefined && !this.onlineFlow ? undefined : !!hasMedicalInsuranceProof,
    };
  }

  private updateStore(): void {
    this.qtLiteStore.updateState({ studentDetails: this.getFormValues() });
  }

  private scrollToTop(): void {
    this.errorMessage.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start', // Scroll to the start of the form
    });
  }

  private initializeTitleOptions() {
    const titles = (this.configuration.QUOTE_TOOL_TITLE_OPTIONS as string).split(',');
    this.titleOptions = titles.map((code) => ({ code, name: this.localizedOptions[code] }));
  }

  private initializeWhatsappOptIn(): void {
    this.whatsappOptIn = <boolean>this.configuration.QUOTE_TOOL_LEAD_WHATSAPP_OPT_IN;
    if (this.whatsappOptIn) {
      this.form.addControl('whatsappOptIn', new FormControl());
    }
  }

  private setupAllergyValidation() {
    const hasAllergiesControl = this.fields.hasAllergiesConditionsOrTreatments;
    const allergyDetailsControl = this.fields.allergiesConditionsOrTreatments;

    // Set validators for 'hasAllergiesConditionsOrTreatments' field
    hasAllergiesControl.setValidators([
      Validators.required,
      allergyValidator(allergyDetailsControl), // Custom validator
    ]);

    // Watch for changes in the 'hasAllergiesConditionsOrTreatments' field
    hasAllergiesControl.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((selectedValue) => {
      this.toggleField('allergiesConditionsOrTreatments', selectedValue); // Enable/Disable the field
      if (selectedValue || selectedValue === false) {
        hasAllergiesControl.markAsTouched();
      }
    });

    // Watch for changes in 'allergiesConditionsOrTreatments' field to update validity of 'hasAllergiesConditionsOrTreatments'
    allergyDetailsControl.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      hasAllergiesControl.updateValueAndValidity();
    });
  }

  private setupAdultFieldListener() {
    this.fields.isAdult.markAsTouched();
    this.fields.isAdult?.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((isAdult) => {
      this.toggleFields(this.guardianFields, !isAdult);
      this.toggleFields(this.emergencyFields, isAdult);
    });
  }

  private toggleFields(fields: string[], enable: boolean) {
    fields.forEach((field) => {
      this.toggleField(field, enable);
    });
  }

  private toggleField(fieldName: string, enable: boolean) {
    const control = this.form.get(fieldName);
    if (control) {
      enable ? control.enable() : control.disable();
    }
  }

  private disableFieldsForOnlineFlow() {
    const toDisable = ['hasAllergiesConditionsOrTreatments', 'allergiesConditionsOrTreatments', 'specialRequests', 'hasMedicalInsuranceProof'];

    toDisable.forEach((field) => {
      this.fields[field].disable();
      this.fields[field].clearValidators();
      this.fields[field].updateValueAndValidity();
    });
  }

  private loadCountryByCode(countryCode: string) {
    return this.residenceDataService.loadResidenceCountries().pipe(
      map((countries) => countries.find((c) => c.code === countryCode)),
      filter(Boolean),
    );
  }

  private loadInitialResidenceData(): void {
    this.qtLiteStore.combinedStudentResidence$
      .pipe(
        take(1),
        switchMap(({ studentDetails, residenceData }) => {
          const countryCode = studentDetails?.countryOfResidence || this.residenceDataService.detectCountryCode();
          const savedCountryCode = residenceData?.countryCode;
          return this.loadCountryByCode(countryCode).pipe(map((country) => ({ country, savedCountryCode, studentDetails })));
        }),
      )
      .subscribe(({ country, savedCountryCode, studentDetails }) => {
        this.applyCountryInfo(country, savedCountryCode);
        this.patchRegionField(studentDetails, country);
        this.applyRegionValidators();
      });
  }

  private handleResidenceCountryChanges(): void {
    const savedCountryCode$ = this.qtLiteStore.residenceData$.pipe(map((residenceData) => residenceData?.countryCode));

    this.fields.countryOfResidence.valueChanges
      .pipe(
        skip(1),
        filter(Boolean),
        switchMap((code) => this.loadCountryByCode(code)),
        withLatestFrom(savedCountryCode$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([country, savedCountryCode]) => {
        this.applyCountryInfo(country, savedCountryCode);
        this.resetRegionFields();
        this.applyRegionValidators();
      });
  }

  private applyCountryInfo(country: ResidenceCountry, savedCountryCode?: string): void {
    const { regions, name, countryRegionType, code } = country;

    this.priceConfirmation = code !== savedCountryCode;
    this.regions = regions || [];
    this.residenceCountry = name;
    this.countryRegionType = countryRegionType;
    this.regionFieldPlaceholder =
      countryRegionType !== CountryRegionTypeEnum.STATE ? $localize`State / Province / Region (optional)` : $localize`State / Province / Region *`;
    this.postalCodeRegion = countryRegionType === CountryRegionTypeEnum.POSTALCODE;
  }

  private patchRegionField(studentDetails: StudentDetails, country: ResidenceCountry): void {
    const regionType = country.countryRegionType;
    const regionCode = studentDetails.regionCode;

    if (!regionCode) {
      return;
    }

    const patch: Partial<Record<keyof typeof this.form.controls, string>> = {
      countryOfResidence: country.code,
    };

    let controlToMark: AbstractControl | null = null;

    switch (regionType) {
      case CountryRegionTypeEnum.CITY:
        patch.city = regionCode;
        controlToMark = this.fields.city;
        break;

      case CountryRegionTypeEnum.POSTALCODE:
        patch.postcode = regionCode;
        controlToMark = this.fields.postcode;
        break;

      case CountryRegionTypeEnum.STATE:
        patch.regionStateProvince = regionCode;
        controlToMark = this.fields.regionStateProvince;
        break;
    }

    this.form.patchValue(patch);

    if (controlToMark) {
      controlToMark.markAsTouched();
      controlToMark.updateValueAndValidity();
    }
  }

  // Reset city and regionStateProvince fields on user country change
  private resetRegionFields(): void {
    this.fields.regionStateProvince.setValue('');
    this.fields.city.setValue('');
  }

  // Setup validators based on current countryRegionType
  private applyRegionValidators(): void {
    const { regionStateProvince, city, postcode } = this.fields;

    regionStateProvince.clearValidators();
    city.clearValidators();
    postcode.clearValidators();

    // city is always required
    city.addValidators([Validators.required]);

    // postcode is always required
    postcode.addValidators([Validators.required, postalCodeValidator(this.postalCodeRegion)]);

    if (this.countryRegionType === CountryRegionTypeEnum.STATE) {
      regionStateProvince.addValidators(Validators.required);
    }

    regionStateProvince.updateValueAndValidity();
    city.updateValueAndValidity();
    postcode.updateValueAndValidity();
  }

  private setPhoneCodeFromStore() {
    this.qtLiteStore.phoneCode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((phoneCode) => {
      this.form.get('emergencyContactPhone')?.setValue(phoneCode, { emitEvent: false });
      this.form.get('guardianPhone')?.setValue(phoneCode, { emitEvent: false });
    });
  }
}
